var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { axios } from "gather-common-including-video/dist/src/public/axios";
import { HttpV2Client, metricsManager as globalsMetricsManager } from "../../Globals";
import { ConsumerKindEnum, } from "gather-video-common/dist/src/public/types";
import * as LK from "livekit-client";
import { RemoteVideoTrack } from "livekit-client";
import { BasePeerManager } from "../../BasePeerManager";
import { debugLogMajorEvent, debugMajorEvents } from "../../DebugLogger";
import { ProduceState, shouldUseHighQualityScreenshare } from "../../utils";
import { Logger } from "../../Logger";
import { metricsManager } from "../../metrics/MetricsManager";
import { ConsumerState, createConsumerInfo } from "../../ConsumerInfo";
import { TWILIO_TOKEN_TTL_S } from "gather-common/dist/src/public/av";
import { ErrorContext } from "gather-common/dist/src/public/utils";
import { getScreenShareMaxBitrate } from "../../config";
import { createLocalLiveKitTrack, getGatherConsumerKind, getLKTrackSource, simpleDataStructureStringify, } from "./utils";
import { tracker } from "../../tracker/Tracker";
import { measureAvLoadtime, VideoPerformanceStages, videoPerformanceTracker, } from "../../videoPerformance";
import { authenticateLiveKitSession } from "./LivekitAuth";
import { PeerManagerEvent } from "../../PeerManagerEvent";
import { LoggableMethodFactory } from "../../log.decorator";
import { Mutex } from "./Mutex";
import base64 from "base-64";
import { HttpV2Paths } from "gather-http-common/dist/src/public/httpAPI";
import { enumKeys } from "gather-common/dist/src/public/ts-utils";
var PermissionStatus = LK.TrackPublication.PermissionStatus;
const logLevel = enumKeys(LK.LogLevel).find((k) => k === process.env.LOG_LEVEL);
LK.setLogLevel(logLevel ?? "debug");
LK.setLogExtension((level, msg, context) => {
    if (level === LK.LogLevel.error) {
        Logger.error("[LK]", new ErrorContext({ msg, context: `${context}` }));
    }
});
const OVER_ALLOW_LIMIT = 10000;
const DISCONNECT_TIMEOUT = 10 * 60 * 1000;
const TURN_CREDENTIALS_INTERVAL_MS = (TWILIO_TOKEN_TTL_S / 2) * 1000;
const FIREWALL_CONN_FAILURES = 2;
const LoggableMethod = LoggableMethodFactory("LivekitPeerManager");
const minimizePublicationLog = (pub) => {
    const base = {
        kind: pub.kind,
        trackSid: pub.trackSid,
        isEnabled: pub.isEnabled,
        isMuted: pub.isMuted,
        track: { streamState: pub.track?.streamState },
    };
    return pub instanceof LK.RemoteTrackPublication
        ? { ...base, currentVideoQuality: pub.videoQuality }
        : base;
};
const minimizeParticipantLog = (participant) => ({
    audioLevel: participant.audioLevel,
    isSpeaking: participant.isSpeaking,
    connectionQuality: participant.connectionQuality,
    sid: participant.sid,
    identity: participant.identity,
    tracks: participant.getTracks(),
    signalClient: participant.signalClient,
    permissions: participant.permissions,
});
export class LivekitPeerManager extends BasePeerManager {
    smallTilePreset;
    mediumTilePreset;
    _room;
    get room() {
        if (!this._room) {
            const roomOptions = {
                dynacast: true,
                adaptiveStream: {
                    pauseVideoInBackground: false,
                },
                publishDefaults: {
                    simulcast: true,
                    red: false,
                    videoSimulcastLayers: [this.smallTilePreset, this.mediumTilePreset],
                    audioBitrate: LK.AudioPresets.music.maxBitrate,
                    screenShareSimulcastLayers: [],
                    forceStereo: false,
                },
                stopLocalTrackOnUnpublish: false,
            };
            globalsMetricsManager.setAttribute("disableRed", roomOptions.publishDefaults?.red ? "false" : "true");
            this._room = new LK.Room(roomOptions);
            this.addEventListeners();
        }
        return this._room;
    }
    sourcesIntendToSubscribeTo = new Map();
    sourcesActuallySubscribedTo = new Map();
    allowedSubscribers = new Set();
    publications = new Map();
    producerPaused = new Map();
    isAllMuted = new Set();
    livekitURL = "";
    livekitToken = "";
    participantId = "";
    spaceId = "";
    localMuteMap = new Map();
    numFailures = 0;
    connectionErrors = 0;
    connectionEverSucceeded = false;
    producerBusyLock;
    connectLock;
    disconnectTimer;
    retryTimer;
    isDestroyed = false;
    roomConnectOptions = { autoSubscribe: false, rtcConfig: {} };
    turnInterval;
    screenShareHighQuality = false;
    publishedScreenShareHighQuality = false;
    selfhost;
    produceScores = {
        audio: undefined,
        video: undefined,
        screen: undefined,
        sound: undefined,
    };
    simulatePausedMap = new Map();
    constructor(statsManager, options) {
        super(statsManager);
        this.producerBusyLock = new Mutex();
        this.connectLock = new Mutex();
        this.rtcStatsManager = statsManager;
        this.selfhost = options.selfhost;
        this.smallTilePreset = new LK.VideoPreset(320, 180, 120000, options.simulcastMaxFramerate);
        this.mediumTilePreset = new LK.VideoPreset(640, 360, 350000, options.simulcastMaxFramerate);
    }
    async start(id, spaceId, _eventManager) {
        if (this._room) {
            Logger.error(`[LK] LivekitPeerManager started already.`);
            return;
        }
        this.participantId = id;
        this.spaceId = spaceId;
        Logger.log(`[LK] start LiveKitPeerManager ${simpleDataStructureStringify({
            participantId: this.participantId,
            spaceId,
        })}`);
        this.getTurnCredentials().catch(() => { });
        this.turnInterval = setInterval(() => {
            this.getTurnCredentials().catch(() => { });
        }, TURN_CREDENTIALS_INTERVAL_MS);
        try {
            await this.ensureHasConnectionInfo();
        }
        catch (e) {
            Logger.error(`[LK] error acquiring token, trying again later`, new ErrorContext({
                data: simpleDataStructureStringify({
                    id,
                    spaceId,
                }),
            }, e));
        }
        this.room.startAudio();
    }
    destroy() {
        debugLogMajorEvent(`[LK] PeerManager destroyed!\n${new Error().stack}`);
        this.removeEventListeners();
        this.room.disconnect(false);
        this.sourcesIntendToSubscribeTo.clear();
        this.sourcesActuallySubscribedTo.clear();
        this.allowedSubscribers.clear();
        this.localMuteMap.clear();
        this.simulatePausedMap.clear();
        this._room = undefined;
        Object.keys(this.info).forEach((key) => {
            this._deleteAndCleanupConsumerInfo(key);
        });
        this.dispatchInfo();
        if (this.turnInterval) {
            clearInterval(this.turnInterval);
        }
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
        }
        this.isDestroyed = true;
    }
    getInfo(id) {
        return this.info[id];
    }
    async produceStart(kind, track, pause) {
        Logger.log(`[LK] produceStart ${kind}`);
        if (kind === "screen") {
            this.screenShareHighQuality = shouldUseHighQualityScreenshare(track);
        }
        this.publications.set(kind, track);
        this.producerPaused.set(kind, pause);
        await this.reconcileProducer();
    }
    async produceEnd(kind) {
        Logger.log(`[LK] produceEnd ${kind}`);
        this.publications.delete(kind);
        this.producerPaused.set(kind, true);
        this.updateProduceScore(kind, undefined);
        this.publishEvent(PeerManagerEvent.ProducerStateChange, kind, ProduceState.Ended);
        await this.reconcileProducer();
    }
    async producePause(kind) {
        Logger.log(`[LK] producePause ${kind}`);
        this.producerPaused.set(kind, true);
        await this.reconcileProducer();
    }
    async produceResume(kind) {
        Logger.log(`[LK] produceResume ${kind}`);
        this.producerPaused.set(kind, false);
        await this.reconcileProducer();
    }
    async subscribe(id, tagSet) {
        Logger.log(`[LK] attempt to subscribe to tracks, ${simpleDataStructureStringify({
            id,
            tags: tagSet,
        })}`);
        if (this.checkOverAllowLimit()) {
            Logger.warn("[LK] over allow subscribe limit");
            return;
        }
        const mutedKinds = new Set([
            ...this.isAllMuted,
            ...(this.localMuteMap.get(id) ?? new Set()),
        ]);
        this.localMuteMap.set(id, mutedKinds);
        const alreadySubscribedTo = this.sourcesIntendToSubscribeTo.get(id);
        this.sourcesIntendToSubscribeTo.set(id, alreadySubscribedTo ? new Set([...alreadySubscribedTo, ...tagSet]) : tagSet);
        Logger.log(`[LK] setting new subscription targets ${simpleDataStructureStringify({
            id,
            sources: this.sourcesIntendToSubscribeTo.get(id),
        })}`);
        try {
            await this.connectToRoom();
            this.reconcileParticipantById(id);
            this.trackAvLoadTimeOnSubscribe(id, tagSet);
        }
        catch (e) {
            this.scheduleRetry(e);
        }
        return;
    }
    _deleteAndCleanupConsumerInfo(id) {
        this.info[id]?.video?.clear();
        this.info[id]?.screenVideo?.clear();
        delete this.info[id];
    }
    unsubscribe(id) {
        Logger.log(`[LK] unsubscribe from all participant tracks ${id}`);
        this.sourcesIntendToSubscribeTo.delete(id);
        this.sourcesActuallySubscribedTo.delete(id);
        this.localMuteMap.delete(id);
        this.simulatePausedMap.delete(id);
        this.reconcileParticipantById(id);
        this._deleteAndCleanupConsumerInfo(id);
        this.dispatchInfo();
        this.disconnectIfEmpty();
    }
    addLatestSpeaker(id) {
        const subscribedKinds = this.sourcesIntendToSubscribeTo.get(id);
        if (subscribedKinds && subscribedKinds.has("audio")) {
            const idInfo = this.info[id];
            if (!idInfo || !idInfo.enabled.audio) {
                globalsMetricsManager.addMetricReliably("gvc-unexpected-audio-active", {
                    srcUserId: id,
                });
            }
        }
    }
    allow(id) {
        Logger.log(`[LK] allow ${id}`);
        this.allowedSubscribers.add(id);
        this.updatePermissions();
    }
    disallow(id) {
        Logger.log(`[LK] disallow ${id}`);
        this.allowedSubscribers.delete(id);
        this.updatePermissions();
    }
    setMute(id, kind, muted) {
        if (!this.localMuteMap.get(id)) {
            this.localMuteMap.set(id, new Set());
        }
        const mutedConsumerKinds = this.localMuteMap.get(id);
        if (muted) {
            mutedConsumerKinds?.add(kind);
        }
        else {
            mutedConsumerKinds?.delete(kind);
        }
        this.reconcileParticipantById(id);
    }
    setAllMuted(kind, muted, exceptedPlayer) {
        Logger.log("[LK] setAllMuted");
        if (muted) {
            this.isAllMuted.add(kind);
        }
        else {
            this.isAllMuted.delete(kind);
        }
        this.room.participants.forEach((p) => {
            if (this.sourcesIntendToSubscribeTo.has(p.identity) && p.identity !== exceptedPlayer) {
                this.setMute(p.identity, kind, muted);
            }
        });
    }
    setMaxConsumerLayers(_id, _kind, _spatialLayer, _temporalLayer) {
        return;
    }
    isSelfHost() {
        return this.selfhost;
    }
    scheduleRetry(e) {
        if (this.isDestroyed) {
            Logger.warn("[LK] attempted to scheduling retry on error after destroyed", { error: e });
            globalsMetricsManager.addMetricReliably("gvc-livekit-scheduling-retry-error", {
                error: "LiveKitPeerManager destroyed",
            });
            return;
        }
        if (e) {
            Logger.warn("[LK] scheduling retry on error", { error: e });
            globalsMetricsManager.addMetricReliably("gvc-livekit-scheduling-retry-error", {
                error: e instanceof LK.LivekitError ? e.code : e instanceof Error ? e.message : String(e),
            });
            this.numFailures++;
        }
        Logger.log("[LK] setting retry timer");
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
        }
        let timeout = Math.max(this.numFailures * this.numFailures * 1000, 300);
        if (timeout > 60000) {
            timeout = 60000;
        }
        this.retryTimer = setTimeout(() => {
            Logger.log("[LK] retrying reconciliation");
            this.reconcileAll();
        }, timeout);
    }
    async reconcileAll() {
        try {
            const needsConnection = this.sourcesIntendToSubscribeTo.size > 0 || this.publications.size > 0;
            if (needsConnection) {
                await this.connectToRoom();
                this.handleRoomUpdate();
                await this.reconcileProducer();
            }
            this.numFailures = 0;
        }
        catch (e) {
            this.scheduleRetry(e);
        }
    }
    async publishScreenshare(localTrack) {
        this.publishedScreenShareHighQuality = this.screenShareHighQuality;
        return this.room.localParticipant.publishTrack(localTrack, {
            screenShareEncoding: {
                maxBitrate: getScreenShareMaxBitrate(this.screenShareHighQuality),
            },
        });
    }
    async publishTrack(localTrack, kind) {
        return await (kind === "screen"
            ? this.publishScreenshare(localTrack)
            : this.room.localParticipant.publishTrack(localTrack));
    }
    async reconcileProducer() {
        const unlock = await this.producerBusyLock.lock();
        Logger.log("[LK] reconciling producer");
        try {
            if (this.publications.size > 0) {
                await this.connectToRoom();
            }
            const sourcesToPublish = new Set();
            for (const [kind, mediaStreamTrack] of this.publications) {
                const source = getLKTrackSource(kind);
                let pub = this.getLocalPublication(kind);
                if (this.producerPaused.get(kind)) {
                    continue;
                }
                sourcesToPublish.add(source);
                try {
                    if (!pub) {
                        this.publishEvent(PeerManagerEvent.ProducerStateChange, kind, ProduceState.Loading);
                        const localTrack = createLocalLiveKitTrack(kind, mediaStreamTrack);
                        Logger.log(`[LK] publish new local track ${simpleDataStructureStringify({
                            source: localTrack.source,
                        })}`);
                        pub = await this.publishTrack(localTrack, kind);
                    }
                    else if (pub.track && pub.track.mediaStreamTrack !== mediaStreamTrack) {
                        await pub.track.replaceTrack(mediaStreamTrack);
                    }
                    else if (kind === "screen" &&
                        this.screenShareHighQuality !== this.publishedScreenShareHighQuality) {
                        let localTrack = pub.track;
                        if (localTrack) {
                            Logger.log("[LK] unpublish local track");
                            this.room.localParticipant.unpublishTrack(localTrack);
                        }
                        else {
                            this.publishEvent(PeerManagerEvent.ProducerStateChange, kind, ProduceState.Loading);
                            localTrack = createLocalLiveKitTrack(kind, mediaStreamTrack);
                        }
                        Logger.log(`[LK] republish local track ${simpleDataStructureStringify({
                            source: localTrack.source,
                        })}`);
                        pub = await this.publishTrack(localTrack, kind);
                    }
                    this.publishEvent(PeerManagerEvent.ProducerStateChange, kind, ProduceState.Connected);
                }
                catch (e) {
                    this.publishEvent(PeerManagerEvent.ProducerStateChange, kind, ProduceState.Errored);
                    this.scheduleRetry(e);
                    continue;
                }
                if (pub?.isUpstreamPaused && !this.producerPaused.get(kind)) {
                    await pub.resumeUpstream();
                }
                if (pub?.isMuted && !this.producerPaused.get(kind)) {
                    await pub.unmute();
                }
            }
            for (const pub of this.room.localParticipant.getTracks()) {
                if (!(pub instanceof LK.LocalTrackPublication)) {
                    continue;
                }
                if (sourcesToPublish.has(pub.source)) {
                    continue;
                }
                if (!pub.isUpstreamPaused) {
                    await pub.pauseUpstream();
                }
            }
            this.disconnectIfEmpty();
        }
        catch (e) {
            this.scheduleRetry(e);
            return;
        }
        finally {
            unlock();
        }
    }
    reconcileParticipantById(id) {
        const participant = this.room.getParticipantByIdentity(id);
        if (participant instanceof LK.RemoteParticipant) {
            this.handleParticipantUpdate(participant);
        }
    }
    getLocalPublication(_kind) {
        const source = getLKTrackSource(_kind);
        const publication = this.room.localParticipant.getTrack(source);
        return publication;
    }
    getSubscribedParticipants() {
        if (!this.room)
            return;
        return Array.from(this.room.participants.values()).filter((participant) => participant.getTracks().some((track) => track.isSubscribed));
    }
    disconnectIfEmpty(force) {
        if (this.disconnectTimer) {
            clearTimeout(this.disconnectTimer);
        }
        if (this.publications.size === 0 && this.sourcesIntendToSubscribeTo.size === 0) {
            if (this.room.state === LK.ConnectionState.Disconnected)
                return;
            if (!force) {
                this.disconnectTimer = setTimeout(() => {
                    this.disconnectIfEmpty(true);
                }, DISCONNECT_TIMEOUT);
                return;
            }
            Logger.log("[LK] no subscriptions or publications present, disconnecting from room");
            this.removeEventListeners();
            metricsManager.mediaInterruption.registerSFU(this.livekitURL);
            this.room.disconnect(false);
            this._room = undefined;
        }
    }
    checkOverAllowLimit() {
        const subscribedParticipants = this.getSubscribedParticipants();
        const isOverAllowed = (subscribedParticipants && subscribedParticipants.length > OVER_ALLOW_LIMIT) ?? false;
        if (isOverAllowed) {
            this.publishEvent(PeerManagerEvent.OverAllow, true);
        }
        return isOverAllowed;
    }
    async getTurnCredentials() {
        const res = await HttpV2Client.get(HttpV2Paths.TwilioTurnCredentials, {});
        if (!res?.turnCredentials) {
            throw Error("Could not retrieve TURN credentials");
        }
        else {
            const token = JSON.parse(res.turnCredentials);
            if (token.iceServers) {
                Logger.log("[LK] successfully retrieved TURN credentials");
                this.roomConnectOptions.rtcConfig = { iceServers: token.iceServers };
            }
        }
    }
    addEventListeners() {
        if (!this.room) {
            Logger.warn("[LK] could not add event listeners, room undefined");
            return;
        }
        this.room
            .on(LK.RoomEvent.ParticipantConnected, this.handleParticipantConnected)
            .on(LK.RoomEvent.ParticipantDisconnected, this.handleParticipantDisconnected)
            .on(LK.RoomEvent.TrackPublished, this.handleTrackPublished)
            .on(LK.RoomEvent.TrackUnpublished, this.handleTrackUnpublished)
            .on(LK.RoomEvent.TrackSubscribed, this.handleTrackSubscribed)
            .on(LK.RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribed)
            .on(LK.RoomEvent.TrackMuted, this.handleTrackMuted)
            .on(LK.RoomEvent.TrackUnmuted, this.handleTrackUnmuted)
            .on(LK.RoomEvent.LocalTrackPublished, this.handleLocalTrackPublished)
            .on(LK.RoomEvent.LocalTrackUnpublished, this.handleLocalTrackUnpublished)
            .on(LK.RoomEvent.TrackStreamStateChanged, this.handleStreamStateChanged)
            .on(LK.RoomEvent.ConnectionQualityChanged, this.handleConnectionQualityChanged)
            .on(LK.RoomEvent.ConnectionStateChanged, this.handleConnectionStateChanged)
            .on(LK.RoomEvent.TrackSubscriptionPermissionChanged, this.handleSubscriptionPermissionsChanged)
            .on(LK.RoomEvent.TrackSubscriptionStatusChanged, this.handleSubscriptionStatusChanged)
            .on(LK.RoomEvent.Reconnected, this.handleReconnected)
            .on(LK.RoomEvent.Disconnected, this.handleDisconnected);
        this.room.engine
            .on(LK.EngineEvent.TransportsCreated, this.handleTransportCreated)
            .on(LK.EngineEvent.SignalResumed, this._refreshWsDisconnectHandler)
            .on(LK.EngineEvent.Restarted, this._refreshWsDisconnectHandler);
    }
    removeEventListeners() {
        if (!this.room) {
            Logger.warn("[LK] could not remove event listeners, room undefined");
            return;
        }
        this.room
            .off(LK.RoomEvent.ParticipantConnected, this.handleParticipantConnected)
            .off(LK.RoomEvent.ParticipantDisconnected, this.handleParticipantDisconnected)
            .off(LK.RoomEvent.TrackPublished, this.handleTrackPublished)
            .off(LK.RoomEvent.TrackUnpublished, this.handleTrackUnpublished)
            .off(LK.RoomEvent.TrackSubscribed, this.handleTrackSubscribed)
            .off(LK.RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribed)
            .off(LK.RoomEvent.TrackMuted, this.handleTrackMuted)
            .off(LK.RoomEvent.TrackUnmuted, this.handleTrackUnmuted)
            .off(LK.RoomEvent.LocalTrackPublished, this.handleLocalTrackPublished)
            .off(LK.RoomEvent.LocalTrackUnpublished, this.handleLocalTrackUnpublished)
            .off(LK.RoomEvent.TrackStreamStateChanged, this.handleStreamStateChanged)
            .off(LK.RoomEvent.ConnectionQualityChanged, this.handleConnectionQualityChanged)
            .off(LK.RoomEvent.ConnectionStateChanged, this.handleConnectionStateChanged)
            .off(LK.RoomEvent.TrackSubscriptionPermissionChanged, this.handleSubscriptionPermissionsChanged)
            .off(LK.RoomEvent.TrackSubscriptionStatusChanged, this.handleSubscriptionStatusChanged)
            .off(LK.RoomEvent.Reconnected, this.handleReconnected)
            .off(LK.RoomEvent.Disconnected, this.handleDisconnected);
        if (this.room.engine) {
            this.room.engine
                .off(LK.EngineEvent.TransportsCreated, this.handleTransportCreated)
                .off(LK.EngineEvent.SignalResumed, this._refreshWsDisconnectHandler)
                .off(LK.EngineEvent.Restarted, this._refreshWsDisconnectHandler);
        }
    }
    handleParticipantConnected = (participant) => {
        Logger.log(`[LK] participant connected ${simpleDataStructureStringify({
            id: participant.identity,
        })}`);
        this.handleParticipantUpdate(participant);
    };
    handleParticipantDisconnected = (participant) => {
        const id = participant.identity;
        Logger.log(`[LK] participant disconnected ${simpleDataStructureStringify({
            id: participant.identity,
        })}`);
        this.handleParticipantUpdate(participant);
        const idInfo = this.info[id];
        if (idInfo) {
            idInfo.state = ConsumerState.ProducerDisconnected;
            this.info[id] = idInfo;
            this.dispatchInfo();
        }
    };
    handleTrackPublished = (publication, participant) => {
        Logger.log(`[LK] track published ${simpleDataStructureStringify({
            id: participant.identity,
            source: publication.source,
            publication: minimizePublicationLog(publication),
        })}`);
        this.handleParticipantUpdate(participant);
    };
    handleTrackUnpublished = (publication, participant) => {
        Logger.log(`[LK] track unpublished ${simpleDataStructureStringify({
            id: participant.identity,
            source: publication.source,
            publication: minimizePublicationLog(publication),
        })}`);
        this.handleParticipantUpdate(participant);
    };
    handleLocalTrackPublished = (pub) => {
        const kind = getGatherConsumerKind(pub.source);
        Logger.log(`[LK] local track published, ${simpleDataStructureStringify({
            publication: minimizePublicationLog(pub),
            shouldPublish: this.publications.get(kind) !== undefined,
            shouldPause: this.producerPaused.get(kind),
        })}`);
        if (this.publications.get(kind) === undefined || this.producerPaused.get(kind)) {
            Logger.log(`[LK] publication ${kind} track race condition detected, will reconcileProducer`);
            this.reconcileProducer();
            return;
        }
    };
    handleLocalTrackUnpublished = (pub) => {
        Logger.log(`[LK] local track un-published, ${simpleDataStructureStringify({
            publication: minimizePublicationLog(pub),
        })}`);
        this.reconcileProducer();
    };
    handleTrackSubscribed = (track, publication, participant) => {
        Logger.log(`[LK] track subscribed ${simpleDataStructureStringify({
            id: participant.identity,
            source: publication.source,
            publication: minimizePublicationLog(publication),
            track,
        })}`);
        this.handleParticipantUpdate(participant);
    };
    handleTrackUnsubscribed = (track, publication, participant) => {
        Logger.log(`[LK] track unsubscribed ${simpleDataStructureStringify({
            id: participant.identity,
            source: publication.source,
            publication: minimizePublicationLog(publication),
            track,
        })}`);
        this.handleParticipantUpdate(participant);
    };
    handleTrackMuted = (publication, participant) => {
        Logger.log(`[LK] track muted ${simpleDataStructureStringify({
            id: participant.identity,
            source: publication.source,
            publication: minimizePublicationLog(publication),
        })}`);
        if (participant instanceof LK.RemoteParticipant) {
            this.handleParticipantUpdate(participant);
        }
    };
    handleTrackUnmuted = (publication, participant) => {
        Logger.log(`[LK] track unmuted ${simpleDataStructureStringify({
            id: participant.identity,
            source: publication.source,
            publication: minimizePublicationLog(publication),
        })}`);
        if (participant instanceof LK.RemoteParticipant) {
            this.handleParticipantUpdate(participant);
        }
    };
    handleStreamStateChanged = (publication, streamState, participant) => {
        Logger.log(`[LK] stream state changed ${simpleDataStructureStringify({
            id: participant.identity,
            source: publication.source,
            state: streamState,
            publication: minimizePublicationLog(publication),
        })}`);
        this.handleParticipantUpdate(participant);
    };
    handleConnectionQualityChanged = (quality, participant) => {
        if (participant instanceof LK.RemoteParticipant) {
            this.handleParticipantUpdate(participant);
        }
        else if (participant instanceof LK.LocalParticipant) {
            this.updateProduceScore("video", this.getConnectionScore(quality));
            this.updateProduceScore("audio", this.getConnectionScore(quality));
        }
    };
    handleConnectionStateChanged = (state) => {
        Logger.log(`[LK] connection state changed ${state}`);
        if (state === LK.ConnectionState.Connected) {
            this.handleRoomUpdate();
            metricsManager.mediaInterruption.signalingInterruptedEvent(this.livekitURL, false);
        }
        else {
            metricsManager.mediaInterruption.signalingInterruptedEvent(this.livekitURL, true);
        }
    };
    handleRoomUpdate = () => {
        this.room.participants.forEach((participant) => {
            this.handleParticipantUpdate(participant);
        });
    };
    handleParticipantUpdate(participant) {
        const id = participant.identity;
        const isSubscribingFrom = this.sourcesIntendToSubscribeTo.has(id);
        const idInfo = isSubscribingFrom ? this.getOrCreateIdInfo(id) : this.info[id];
        if (!idInfo)
            return;
        let allActuallySubscribedTracksPresent = true;
        participant.getTracks().forEach((publication) => {
            const gatherConsumerKind = getGatherConsumerKind(publication.source);
            if (publication instanceof LK.RemoteTrackPublication) {
                const shouldBeSubscribed = this.sourcesIntendToSubscribeTo.get(id)?.has(gatherConsumerKind) ?? false;
                if (publication.isSubscribed !== shouldBeSubscribed) {
                    if (shouldBeSubscribed) {
                        idInfo.state = ConsumerState.Consuming;
                    }
                    const sourceSet = this.sourcesActuallySubscribedTo.get(id) ?? new Set();
                    shouldBeSubscribed
                        ? sourceSet.add(gatherConsumerKind)
                        : sourceSet.delete(gatherConsumerKind);
                    this.sourcesActuallySubscribedTo.set(id, sourceSet);
                    publication.setSubscribed(shouldBeSubscribed);
                    Logger.log(`[LK] setting publication as ${shouldBeSubscribed ? "subscribed" : "unsubscribed"}, ${simpleDataStructureStringify({
                        gatherConsumerKind,
                        isSubscribed: publication.isSubscribed,
                    })}`);
                }
            }
            if (this.sourcesActuallySubscribedTo.get(id)?.has(gatherConsumerKind) && !publication.track) {
                allActuallySubscribedTracksPresent = false;
            }
            let streamStatePaused = false;
            if (publication.track?.kind === LK.Track.Kind.Video) {
                streamStatePaused = this.simulatePausedMap.get(id)?.has(gatherConsumerKind)
                    ? true
                    : publication.track?.streamState === LK.Track.StreamState.Paused;
                if (publication.track?.source === LK.Track.Source.Camera) {
                    idInfo.streamStatePaused = {
                        ...idInfo.streamStatePaused,
                        video: streamStatePaused,
                    };
                }
                else {
                    idInfo.streamStatePaused = {
                        ...idInfo.streamStatePaused,
                        screen: streamStatePaused,
                    };
                }
            }
            const mutedKinds = this.localMuteMap.get(id);
            const isMuted = !!mutedKinds?.has(gatherConsumerKind);
            if (publication.isSubscribed &&
                publication.track &&
                !publication.isMuted &&
                !streamStatePaused &&
                !isMuted) {
                this.attachPublication(participant, publication, idInfo);
            }
            else if (!publication.isSubscribed ||
                ((publication.isMuted || streamStatePaused || isMuted) &&
                    publication.kind !== LK.Track.Kind.Audio)) {
                const kind = publication.kind === LK.Track.Kind.Audio ? "audio" : "video";
                let playerMediaSource;
                let elementInfo;
                if (publication.source === LK.Track.Source.Microphone ||
                    publication.source === LK.Track.Source.Camera) {
                    playerMediaSource = idInfo.video;
                    elementInfo = this.videoElementInfo.get(id);
                }
                else {
                    playerMediaSource = idInfo.screenVideo;
                    elementInfo = this.screenElementInfo.get(id);
                }
                playerMediaSource?.removeTrack(kind);
                if (elementInfo && publication.track instanceof LK.RemoteVideoTrack) {
                    publication.track.stopObservingElementInfo(elementInfo);
                }
            }
            idInfo.enabled[gatherConsumerKind] = !publication.isMuted;
            if (publication.source === LK.Track.Source.Camera) {
                idInfo.videoEnabled = !publication.isMuted;
            }
            idInfo.muted[gatherConsumerKind] = isMuted;
            if (idInfo.video && (gatherConsumerKind === "video" || gatherConsumerKind === "audio")) {
                this.setPlayerMediaSourceLocallyMuted(idInfo.video, publication, isMuted);
            }
            else if (idInfo.screenVideo) {
                this.setPlayerMediaSourceLocallyMuted(idInfo.screenVideo, publication, isMuted);
            }
            idInfo.producerScores[gatherConsumerKind] = this.getConnectionScore(participant.connectionQuality);
        });
        if (allActuallySubscribedTracksPresent) {
            idInfo.state = ConsumerState.Connected;
        }
        this.info[id] = idInfo;
        this.dispatchInfo();
    }
    setPlayerMediaSourceLocallyMuted(mediaSource, pub, muted) {
        if (!(pub instanceof LK.RemoteTrackPublication))
            return;
        if (pub.kind === LK.Track.Kind.Video) {
            mediaSource.setMute("consumer", "video", muted);
        }
        else if (pub.kind === LK.Track.Kind.Audio) {
            mediaSource.setMute("consumer", "audio", muted);
            pub.setEnabled(!muted);
        }
    }
    updateProduceScore(kind, score) {
        if (this.produceScores[kind] !== score) {
            this.produceScores[kind] = score;
            this.publishEvent(PeerManagerEvent.ProduceScores, { ...this.produceScores });
        }
    }
    getConnectionScore(quality) {
        switch (quality) {
            case LK.ConnectionQuality.Poor:
                return 3;
            case LK.ConnectionQuality.Good:
                return 6;
            case LK.ConnectionQuality.Excellent:
                return 10;
            default:
                return 10;
        }
    }
    getOrCreateIdInfo(identity) {
        let idInfo = this.info[identity];
        if (!idInfo) {
            idInfo = createConsumerInfo(ConsumerState.Connected, identity);
        }
        for (const kind of ConsumerKindEnum) {
            idInfo.enabled[kind] = false;
            idInfo.muted[kind] = false;
        }
        idInfo.videoEnabled = false;
        return idInfo;
    }
    handleReconnected = () => {
        this.updatePermissions();
    };
    handleDisconnected = (reason) => {
        if (reason === LK.DisconnectReason.DUPLICATE_IDENTITY) {
            this.publishEvent(PeerManagerEvent.DisableVideo);
            this.destroy();
        }
        else if (reason !== LK.DisconnectReason.CLIENT_INITIATED) {
            Logger.warn(`[LK] room disconnected reason: ${reason}`);
            globalsMetricsManager.addMetricReliably("gvc-livekit-disconnect", {
                reason: Number(reason),
            });
            Object.keys(this.info).forEach((id) => {
                const idInfo = this.info[id];
                if (idInfo) {
                    idInfo.state = ConsumerState.Consuming;
                }
            });
            this.dispatchInfo();
        }
    };
    handleTransportCreated = (publisher, subscriber) => {
        publisher.pc.addEventListener?.("connectionstatechange", () => {
            const connState = this.room?.engine.publisher?.pc?.connectionState || "closed";
            metricsManager.connectionState.countStateChange(this.livekitURL, connState, "send");
            metricsManager.mediaInterruption.mediaInterruptedEvent(this.livekitURL, "send", connState === "connected" ? false : true);
        });
        subscriber.pc.addEventListener?.("connectionstatechange", () => {
            const connState = this.room?.engine.subscriber?.pc?.connectionState || "closed";
            metricsManager.connectionState.countStateChange(this.livekitURL, connState, "recv");
            metricsManager.mediaInterruption.mediaInterruptedEvent(this.livekitURL, "recv", connState === "connected" ? false : true);
        });
    };
    _recordConnectionFailed() {
        this.connectionErrors++;
        Logger.log(`Connection errors: ${this.connectionErrors}`);
        if (this.connectionErrors === FIREWALL_CONN_FAILURES && !this.connectionEverSucceeded) {
            this.publishEvent(PeerManagerEvent.FirewallIssue, true);
        }
    }
    _recordConnectionSucceeded() {
        if (!this.connectionEverSucceeded) {
            this.connectionEverSucceeded = true;
            this.publishEvent(PeerManagerEvent.FirewallIssue, false);
        }
    }
    async ensureHasConnectionInfo() {
        let isInvalid = true;
        if (this.livekitToken && this.livekitURL) {
            const tokenPayload = this.livekitToken.split(".")[1];
            if (tokenPayload) {
                const tokenExpirationTime = JSON.parse(base64.decode(tokenPayload)).exp;
                isInvalid = Date.now() >= tokenExpirationTime * 1000;
            }
        }
        if (isInvalid) {
            Logger.log("[LK] invalid token or url, getting a new one");
            const { serverURL, token } = await authenticateLiveKitSession(this.participantId, this.spaceId, this.selfhost);
            this.livekitURL = serverURL;
            this.livekitToken = token;
            if (this.livekitURL) {
                const url = this.livekitURL.replace("wss:", "https:").replace("ws:", "http:");
                axios
                    .get(url)
                    .then(() => {
                    this._recordConnectionSucceeded();
                })
                    .catch((e) => {
                    this._recordConnectionFailed();
                    Logger.error(`Error trying to get url - error to warm SSL cache`, new ErrorContext({ url }, e));
                });
            }
        }
        if (this.livekitToken !== "" && this.livekitURL !== "")
            return true;
        return new Promise((resolve, reject) => {
            const checkInterval = setInterval(() => {
                if (this.livekitToken !== "" && this.livekitURL !== "") {
                    clearInterval(checkInterval);
                    clearTimeout(timeout);
                    resolve(true);
                }
            }, 100);
            const timeout = setTimeout(() => {
                if (this.livekitToken !== "" && this.livekitURL !== "") {
                    clearInterval(checkInterval);
                    clearTimeout(timeout);
                    Logger.warn("[LK] token acquire timeout");
                    reject();
                }
            }, 15000);
        });
    }
    async connectToRoom() {
        if (this.isDestroyed) {
            Logger.log("[LK] peer manager has been previously destroyed");
            return;
        }
        if (this.disconnectTimer) {
            clearTimeout(this.disconnectTimer);
        }
        const unlock = await this.connectLock.lock();
        if (this.room.state === LK.ConnectionState.Reconnecting) {
            try {
                await new Promise((resolve, reject) => {
                    const handleReconnect = () => {
                        clearTimeout(reconnectTimeout);
                        this._recordConnectionSucceeded();
                        resolve(true);
                    };
                    const reconnectTimeout = setTimeout(() => {
                        this.room.off(LK.RoomEvent.Reconnected, handleReconnect);
                        reject();
                    }, 3000);
                    this.room.once(LK.RoomEvent.Reconnected, handleReconnect);
                });
            }
            catch (e) {
            }
        }
        if (this.room.state === LK.ConnectionState.Connected) {
            unlock();
            return;
        }
        try {
            await this.ensureHasConnectionInfo();
            if (this.isDestroyed) {
                Logger.log("[LK] peer manager has been previously destroyed");
                unlock();
                return;
            }
            Logger.log("[LK] connecting to room");
            try {
                await this.room.connect(this.livekitURL, this.livekitToken, this.roomConnectOptions);
                this._recordConnectionSucceeded();
            }
            catch (e) {
                this._recordConnectionFailed();
                throw e;
            }
            Logger.log("[LK] connected to room");
            tracker.setMetadataAll({
                liveKitSessionId: this.room.sid,
                liveKitParticipantId: this.room.localParticipant.sid,
            });
            metricsManager.mediaInterruption.unregisterSFU(this.livekitURL);
            metricsManager.mediaInterruption.registerSFU(this.livekitURL);
            this._refreshWsDisconnectHandler();
            this.updatePermissions();
            this.reconcileProducer();
            const getSfuClients = () => {
                const minimalSfuClients = {};
                minimalSfuClients[this.livekitURL] = {
                    serverURL: this.livekitURL,
                    sendTransport: this.room.engine?.publisher?.pc,
                    recvTransport: this.room.engine?.subscriber?.pc,
                };
                return minimalSfuClients;
            };
            this.rtcStatsManager.enableStatsCollection(getSfuClients);
            this.handleRoomUpdate();
        }
        finally {
            unlock();
        }
    }
    _refreshWsDisconnectHandler = () => {
        if (this.room.engine.client.ws) {
            const onError = this.room.engine.client.ws.onerror?.bind(this.room.engine.client.ws);
            this.room.engine.client.ws.onerror = (ev) => {
                Logger.warn(`[LK] Websocket error! ${ev}`);
                globalsMetricsManager.addMetricReliably("gvc-livekit-websocket-error");
                if (onError) {
                    onError?.(ev);
                }
            };
        }
    };
    attachPublication(participant, publication, idInfo) {
        if (!publication.track || !idInfo.video || !idInfo.screenVideo)
            return;
        const tracks = [
            idInfo.video.audioTrack,
            idInfo.video.videoTrack,
            idInfo.screenVideo.audioTrack,
            idInfo.screenVideo.videoTrack,
        ];
        if (tracks.some((track) => publication.track && track === publication.track.mediaStreamTrack)) {
            return;
        }
        Logger.log(`[LK] attaching track to participant ${simpleDataStructureStringify({
            participant: minimizeParticipantLog(participant),
            publication: minimizePublicationLog(publication),
        })}`);
        const skipPlay = {
            video: false,
            screen: false,
        };
        const playerMediaSource = publication.source === LK.Track.Source.Camera ||
            publication.source === LK.Track.Source.Microphone
            ? idInfo.video
            : idInfo.screenVideo;
        playerMediaSource.addTrack(publication.track.mediaStreamTrack);
        const measurement = measureAvLoadtime(participant.identity, getGatherConsumerKind(publication.source), participant.identity);
        if (measurement) {
            globalsMetricsManager.addMetricReliably("avLoadTime", measurement);
        }
        if (publication.source === LK.Track.Source.Camera) {
            const elementInfo = this.videoElementInfo.get(participant.identity);
            if (elementInfo && publication?.track instanceof RemoteVideoTrack) {
                publication.track.observeElementInfo(elementInfo);
            }
            skipPlay.video = true;
        }
        else if (publication.track.source === LK.Track.Source.ScreenShare) {
            const elementInfo = this.screenElementInfo.get(participant.identity);
            if (elementInfo && publication?.track instanceof RemoteVideoTrack) {
                publication.track.observeElementInfo(elementInfo);
            }
            skipPlay.screen = true;
        }
        if (!skipPlay.video) {
            idInfo.video?.play();
        }
        if (!skipPlay.screen) {
            idInfo.screenVideo?.play();
        }
    }
    handleSubscriptionStatusChanged = (pub, status, participant) => {
        Logger.log(`[LK] subscription status changed  ${simpleDataStructureStringify({
            id: participant.identity,
            kind: pub.kind,
            status,
        })}`);
        const idInfo = this.info[participant.identity];
        if (idInfo) {
            this.reconcileParticipantById(participant.identity);
        }
    };
    handleSubscriptionPermissionsChanged = (pub, status, participant) => {
        Logger.log(`[LK] subscription permissions changed, ${simpleDataStructureStringify({
            id: participant.identity,
            kind: pub.kind,
            status,
        })}`);
        const idInfo = this.info[participant.identity];
        if (idInfo) {
            if (status === PermissionStatus.NotAllowed) {
                idInfo.state = ConsumerState.NotAllowed;
                this.dispatchInfo();
            }
            else {
                this.handleParticipantUpdate(participant);
            }
        }
    };
    updatePermissions() {
        const participantPermissions = Array.from(this.allowedSubscribers).map((id) => ({
            participantIdentity: id,
            allowAll: true,
        }));
        this.room.localParticipant.setTrackSubscriptionPermissions(false, participantPermissions);
    }
    dispatchInfo() {
        this.publishEvent(PeerManagerEvent.Info, this.info);
    }
    trackAvLoadTimeOnSubscribe(id, tagSet) {
        const participant = this.room.getParticipantByIdentity(id);
        if (!participant) {
            tagSet.forEach((kind) => {
                videoPerformanceTracker.clearMarksAndMeasures(id, kind);
            });
        }
        else if (participant instanceof LK.RemoteParticipant) {
            const publishedTracks = participant.getTracks();
            tagSet.forEach((kind) => {
                if (!publishedTracks.map((pub) => getGatherConsumerKind(pub.source)).includes(kind)) {
                    videoPerformanceTracker.clearMarksAndMeasures(id, kind);
                }
            });
            publishedTracks.forEach((pub) => {
                if (!pub.isSubscribed && !pub.isMuted) {
                    videoPerformanceTracker.mark(VideoPerformanceStages.ConsumeStart, id, getGatherConsumerKind(pub.source));
                }
                else if (pub.isMuted) {
                    videoPerformanceTracker.clearMarksAndMeasures(id, getGatherConsumerKind(pub.source));
                }
            });
        }
    }
    connectToServerIfNotConnected() {
        if (this.room.state === LK.ConnectionState.Disconnected) {
            this.connectToRoom().catch(Logger.error);
        }
    }
    _forceReSubscription() {
        const mediaSet = new Set(["audio", "video", "screen"]);
        this.room.participants.forEach((p) => {
            this.disallow(p.identity);
            this.unsubscribe(p.identity);
        });
        this.room.participants.forEach((p) => {
            this.allow(p.identity);
            this.subscribe(p.identity, mediaSet);
        });
    }
    async _forceReProduce() {
        const mediaSet = new Set(["audio", "video", "screen"]);
        const trackMap = new Map();
        for (const kind of mediaSet) {
            const currentPublication = this.getLocalPublication(kind);
            const mediaStreamTrack = currentPublication?.track?.mediaStreamTrack;
            if (currentPublication && !currentPublication.track?.isMuted && mediaStreamTrack) {
                trackMap.set(kind, mediaStreamTrack);
                this.produceEnd(kind);
            }
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
        for (const [kind, track] of trackMap) {
            this.produceStart(kind, track, false);
        }
    }
    _forceReEnter() {
        this._forceReProduce();
        this._forceReSubscription();
    }
    async debugInfo() {
        return {
            summary: await this._summarize(),
        };
    }
    getWebrtcConnectionInfo(id) {
        if (id === this.participantId) {
            const pc = this.room.engine.publisher?.pc;
            const info = {
                serverURL: this.livekitURL,
                transceivers: new Map(),
                maxSpatialLayer: new Map(),
                connectionState: pc?.connectionState ?? "closed",
                iceTransportPolicy: pc?.getConfiguration().iceTransportPolicy ?? "all",
            };
            for (const [kind, track] of this.publications) {
                const sender = pc?.getSenders()?.filter((s) => s.track?.id === track.id)[0];
                info.transceivers.set(kind, sender);
            }
            return info;
        }
        const remoteParticipant = this.getSubscribedParticipants()?.filter((p) => p.identity === id)[0];
        if (remoteParticipant) {
            const pc = this.room.engine.subscriber?.pc;
            const info = {
                serverURL: this.livekitURL,
                maxSpatialLayer: new Map(),
                transceivers: new Map(),
                connectionState: pc?.connectionState ?? "closed",
                iceTransportPolicy: pc?.getConfiguration().iceTransportPolicy ?? "all",
            };
            remoteParticipant.getTracks().forEach((track) => {
                const kind = getGatherConsumerKind(track.source);
                const receivers = pc?.getReceivers();
                const receiver = receivers?.filter((r) => r.track.id === track.track?.mediaStreamTrack.id)[0];
                info.transceivers.set(kind, receiver);
            });
            return info;
        }
        return;
    }
    simulateScenario(scenario, id, kind, paused) {
        if (scenario === "paused") {
            this.simulatePaused(id, kind, paused);
        }
        else {
            this.room.simulateScenario(scenario);
        }
    }
    simulatePaused(id, kind, paused) {
        if (!this.simulatePausedMap.get(id)) {
            this.simulatePausedMap.set(id, new Set());
        }
        const pausedConsumerKinds = this.simulatePausedMap.get(id);
        if (paused) {
            pausedConsumerKinds?.add(kind);
        }
        else {
            pausedConsumerKinds?.delete(kind);
        }
        this.reconcileParticipantById(id);
    }
    startObserve(id, elementInfo, kind) {
        const participant = this.room.getParticipantByIdentity(id);
        const publication = participant?.getTrack(getLKTrackSource(kind));
        if (publication?.track instanceof RemoteVideoTrack) {
            publication?.track.observeElementInfo(elementInfo);
        }
        if (kind === "video") {
            this.videoElementInfo.set(id, elementInfo);
        }
        else {
            this.screenElementInfo.set(id, elementInfo);
        }
    }
    stopObserve(id, kind) {
        const participant = this.room.getParticipantByIdentity(id);
        const publication = participant?.getTrack(getLKTrackSource(kind));
        const elementInfo = kind === "video" ? this.videoElementInfo.get(id) : this.screenElementInfo.get(id);
        if (elementInfo && publication?.track instanceof RemoteVideoTrack) {
            publication?.track.stopObservingElementInfo(elementInfo);
        }
        if (kind === "video") {
            this.videoElementInfo.delete(id);
        }
        else {
            this.screenElementInfo.delete(id);
        }
    }
    dumpRTCPeerConnection() {
        const pcPublisher = this.room.engine.publisher?.pc;
        const pcSubscriber = this.room.engine.subscriber?.pc;
        return {
            publisher: pcPublisher
                ? {
                    connectionState: pcPublisher.connectionState,
                    iceConnectionState: pcPublisher.iceConnectionState,
                    iceGatheringState: pcPublisher.iceGatheringState,
                    signalingState: pcPublisher.signalingState,
                }
                : {},
            subscriber: pcSubscriber
                ? {
                    connectionState: pcSubscriber.connectionState,
                    iceConnectionState: pcSubscriber.iceConnectionState,
                    iceGatheringState: pcSubscriber.iceGatheringState,
                    signalingState: pcSubscriber.signalingState,
                }
                : {},
        };
    }
    async _summarize() {
        try {
            const subscribedParticipants = this.getSubscribedParticipants();
            const subscribed = {};
            subscribedParticipants?.forEach((participant) => {
                const subscribedTrackKinds = participant
                    .getTracks()
                    .map((track) => getGatherConsumerKind(track.source));
                subscribed[participant.identity] = subscribedTrackKinds;
            });
            const intendSubscribe = {};
            for (const [id, kinds] of this.sourcesIntendToSubscribeTo.entries()) {
                intendSubscribe[id] = [...kinds];
            }
            const mutedUsers = {};
            this.localMuteMap.forEach((value, key) => {
                mutedUsers[key] = Array.from(value);
            });
            const muted = {
                mutedAll: Array.from(this.isAllMuted),
                mutedUsers,
            };
            const info = {};
            Object.entries(this.info).forEach(([id, idInfo]) => {
                info[id] = idInfo.summarize();
            });
            const remoteParticipants = {};
            this.room.participants.forEach((participant) => {
                const id = participant.identity;
                remoteParticipants[id] = participant.getTracks().map((track) => ({
                    kind: getGatherConsumerKind(track.source),
                    subscribed: track.isSubscribed,
                    muted: track.isMuted,
                    enabled: track.isEnabled,
                    streamState: track.track?.streamState,
                }));
            });
            return {
                peerManagerStrategy: "Livekit",
                id: this.participantId,
                spaceId: this.room.name,
                routerURL: this.livekitToken,
                routerConnection: this.room.state,
                subscribed,
                intendSubscribe,
                allowed: Array.from(this.allowedSubscribers),
                muted,
                localParticipant: this.room.localParticipant.sid,
                roomSessionId: this.room.sid,
                info,
                debugMajorEvents,
                remoteParticipants,
                rtcPeerConnections: this.dumpRTCPeerConnection(),
            };
        }
        catch (e) {
            Logger.error("[LK] failed to create summary", e);
            return {
                error: e,
            };
        }
    }
}
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "start", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "destroy", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "produceStart", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "produceEnd", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "producePause", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "produceResume", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "subscribe", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "unsubscribe", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "allow", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "disallow", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "setMute", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "setAllMuted", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "setMaxConsumerLayers", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "scheduleRetry", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "reconcileAll", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "publishScreenshare", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "publishTrack", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "reconcileProducer", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "reconcileParticipantById", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "getLocalPublication", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "getSubscribedParticipants", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "disconnectIfEmpty", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "checkOverAllowLimit", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "getTurnCredentials", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "setPlayerMediaSourceLocallyMuted", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "_recordConnectionFailed", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "_recordConnectionSucceeded", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "ensureHasConnectionInfo", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "connectToRoom", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "attachPublication", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "updatePermissions", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "trackAvLoadTimeOnSubscribe", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "_forceReProduce", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "simulateScenario", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "startObserve", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "stopObserve", null);
__decorate([
    LoggableMethod
], LivekitPeerManager.prototype, "_summarize", null);
//# sourceMappingURL=LivekitPeerManager.js.map